import { getServiceRegistry } from '../services/serviceRegistry';
import { NetworkRequestConfig, HttpMethod } from '../services/interfaces/network.types';

/**
 * Legacy API Client Options
 */
export interface LegacyApiClientOptions extends Omit<RequestInit, 'method' | 'headers' | 'signal'> {
  data?: unknown;
  params?: Record<string, string | number | boolean | undefined | string[] | number[]>;
  headers?: Record<string, string>;
  method?: string;
  signal?: AbortSignal;
  timeout?: number;
  idempotencyKey?: string;
  responseType?: 'blob' | 'json' | 'text';
}

/**
 * apiClient - Universal Platform-Agnostic Proxy
 * 
 * DESIGN RATIONALE:
 * - Maintains backward compatibility with existing API services and components.
 * - Forwards all requests to the platform-specific injected NetworkService.
 * - Fulfills Phase 3 "Platform-Agnostic Business Logic" by decoupling the Data Layer from DOM/Web APIs.
 * - Automatically benefits from Idempotency and Circuit Breaker logic within the service implementation.
 */
async function apiClient<T = unknown>(
  endpoint: string,
  options: LegacyApiClientOptions = {}
): Promise<T> {
  const registry = getServiceRegistry();
  const network = registry.network;

  // Map legacy options to the new platform-agnostic NetworkRequestConfig format
  const config: NetworkRequestConfig = {
    url: endpoint,
    method: (options.method?.toUpperCase() as HttpMethod) || 'GET',
    data: options.data,
    params: options.params,
    headers: options.headers,
    signal: options.signal,
    timeout: options.timeout,
    idempotencyKey: options.idempotencyKey,
    withCredentials: options.credentials !== 'omit', // Map fetch credentials to boolean
    responseType: options.responseType,
  };

  const response = await network.request<T>(config);
  return response.data;
}

/**
 * Static accessors for the active auth token.
 * Forwards to the injected NetworkService.
 */
export const setToken = (token: string | null, userId?: string): void => {
  getServiceRegistry().network.setAuthToken(token, userId);
};

export const getToken = (): string | null => {
  return getServiceRegistry().network.getAuthToken();
};

export const clearToken = (): void => {
  getServiceRegistry().network.setAuthToken(null);
};

export const markSessionExpired = (): void => {
  getServiceRegistry().network.markSessionExpired();
};

export const refreshAuthToken = async (originalToken?: string | null): Promise<string | null> => {
  return await getServiceRegistry().network.refreshAuthToken(originalToken);
};

// Convenience methods with strict typing
apiClient.get = <T = unknown>(endpoint: string, options: LegacyApiClientOptions = {}) =>
  apiClient<T>(endpoint, { ...options, method: 'GET' });

apiClient.post = <T = unknown>(endpoint: string, data?: unknown, options: LegacyApiClientOptions = {}) =>
  apiClient<T>(endpoint, { ...options, method: 'POST', data });

apiClient.put = <T = unknown>(endpoint: string, data?: unknown, options: LegacyApiClientOptions = {}) =>
  apiClient<T>(endpoint, { ...options, method: 'PUT', data });

apiClient.patch = <T = unknown>(endpoint: string, data?: unknown, options: LegacyApiClientOptions = {}) =>
  apiClient<T>(endpoint, { ...options, method: 'PATCH', data });

apiClient.delete = <T = unknown>(endpoint: string, options: LegacyApiClientOptions = {}) =>
  apiClient<T>(endpoint, { ...options, method: 'DELETE' });

export default apiClient;
